import { generateCustomSpec } from "@/utils/helper";
import { TableLowcode } from "alurkerja-ui";
import { getErrorBackground } from "./utils/getErrorBackground";
import { H4 } from "@/components/ui/typography";
import { DownloadButton } from "@/components/ui/button";
import { useAPDValidation } from "@/api/trackerdata-apd";
import { swalError } from "@/utils/lib/swal";
import { AxiosError } from "axios";
import { generateFilenameWithoutMinute } from "@/utils/helper/generateFIlename";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface APDValidationTableProps {
  errorRows: any[];
  generalMessages: any[];
}

export default function APDValidationTable(props: APDValidationTableProps) {
  const { errorRows, generalMessages } = props;

  return (
    <div>
      {(Boolean(errorRows.length) || Boolean(generalMessages.length)) && (
        <div className="mb-2 w-full border-t py-2">
          <H4>APD Validation Result</H4>
        </div>
      )}
      {Boolean(errorRows.length) && (
        <TableLowcode
          baseUrl=""
          spec={spec}
          data={errorRows}
          headerElement={
            <HeaderElement
              errorRows={errorRows}
              generalMessages={generalMessages}
            />
          }
          customCell={CustomCell}
          enableResponsive={false}
          customButtonFilter={() => <></>}
          hideActionColumn
          hideNumberIteration
        />
      )}

      {Boolean(generalMessages.length) && (
        <div>
          <div className="mb-2 mt-10 font-medium leading-none tracking-tight">
            General Validation
          </div>
          <ol className="list-inside list-decimal text-sm">
            {generalMessages?.map((message) => (
              <li key={message.id}>{message.message}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

const HeaderElement = (props: APDValidationTableProps) => {
  const { errorRows, generalMessages } = props;
  const { mutate, isPending } = useAPDValidation();

  const handleDownload = () => {
    mutate(
      {
        data: {
          warnMessages: errorRows,
          warnMessageAllRows: generalMessages,
        },
        fileName: generateFilenameWithoutMinute({
          name: "ListValidationAPD",
          extension: "xlsx",
        }),
      },
      {
        onError: (error: Error) => {
          console.error(error);
          const err = error as AxiosError;
          const errorMessage = (err.response?.data as { data: unknown })?.data;

          swalError.fire({
            title: "Download failed",
            text: `${errorMessage}`,
          });
        },
      },
    );
  };

  return (
    <DownloadButton onClick={handleDownload} loading={isPending}>
      Download
    </DownloadButton>
  );
};

function CustomCell({
  name,
  rowValue,
  rowIndex,
}: {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowValue: { [x: string]: any };
  rowIndex: number;
}) {
  if (name === "custom_no") {
    return <>{rowIndex + 1}</>;
  }

  if (name === "message") {
    const messages = rowValue?.warnMessageDetails
      ?.map((msg: { message: string }) => msg.message)
      .join(", ");

    return <div className="w-44">{messages}</div>;
  }

  return (
    <div className={getErrorBackground(rowValue, name)}>
      {rowValue?.[name]?.value ?? "-"}
    </div>
  );
}

const spec = generateCustomSpec({
  path: "",
  canBulk: false,
  canEdit: false,
  fields: [
    {
      name: "message",
      label: "Message",
    },
    {
      name: "cityTown",
      label: "City Town",
    },
    {
      name: "suffixPermit",
      label: "Suffix Permit",
    },
    {
      name: "hpPermit",
      label: "HP Permit",
    },
    {
      name: "drawingIdApd",
      label: "Drawing ID APD",
    },
    {
      name: "clusterId",
      label: "Cluster ID",
    },
    {
      name: "projectName",
      label: "Project Name",
    },
    {
      name: "hpDesign",
      label: "HP Design",
    },
    {
      name: "latitude",
      label: "LAT",
    },
    {
      name: "longitude",
      label: "LONG",
    },
    {
      name: "fdtId",
      label: "FDT ID",
    },
    {
      name: "oltType",
      label: "OLT Type",
    },
    {
      name: "oltIdaction",
      label: "OLT ID",
    },
    {
      name: "combineStatus",
      label: "Combine Status",
    },
    {
      name: "tenantName",
      label: "Tenant Name",
    },
    {
      name: "vendor",
      label: "Vendor",
    },
    {
      name: "rollOutType",
      label: "Roll-Out Type",
    },
    {
      name: "profileName",
      label: "Profile Name",
    },
    {
      name: "monthTargetString",
      label: "Month Target",
    },
    {
      name: "yearTargetString",
      label: "Year Target",
    },
  ],
});
